import React, { useEffect, useRef, useState } from 'react';
import { InputText, Button, useDebounce } from '@insurancemenu/imenu-components-lib';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { createStyles } from './styles';

export interface SearchInfo {
  name: string;
  id: string;
}

interface SearchBlockProps {
  filteredInfo: SearchInfo[];
  inputLabel: string;
  placeholder?: string;
  className?: string;
  resetFilteredItems: () => void;
  searchByValue: (value: string) => void;
  selectFromSearch: (info: SearchInfo) => void;
}

const SearchBlock: React.FC<SearchBlockProps> = observer((props) => {
  const { inputLabel, filteredInfo, placeholder, className, resetFilteredItems, searchByValue, selectFromSearch } =
    props;

  const [searchValue, setSearchValue] = useState('');
  const debouncedValue = useDebounce<string>(searchValue);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (debouncedValue) {
      searchByValue(debouncedValue);
    }
  }, [debouncedValue]);

  const resetSearch = (): void => {
    setSearchValue('');
    resetFilteredItems();
  };

  const selectAgencyFromSearch = (info: SearchInfo): void => {
    selectFromSearch(info);
    resetSearch();
  };

  const handleClickOutside = (e: MouseEvent): void => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      resetSearch();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const classes = createStyles();
  return (
    <div className={clsx(classes.searchBlock, className)} ref={containerRef}>
      <InputText
        name="searchBlock"
        label={inputLabel}
        value={searchValue}
        description={<span className={classes.searchInputDescription}>{`Matches: ${filteredInfo.length}`}</span>}
        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
          setSearchValue(e.target.value);
        }}
        placeholder={placeholder || ''}
        htmlType="search"
        className={classes.searchInput}
        width="30rem"
      />
      {filteredInfo.length > 0 && (
        <ul className={classes.filterList}>
          {filteredInfo.map((info) => (
            <li
              key={info.id}
              className={classes.filterItem}
              role="menuitem"
              tabIndex={0}
              onKeyDown={(): void => {
                selectAgencyFromSearch(info);
              }}
              onClick={(): void => {
                selectAgencyFromSearch(info);
              }}
            >
              <span>{info.name}</span>
              <Button
                type="text"
                color="primary"
                size="small"
                onClick={(): void => {
                  selectAgencyFromSearch(info);
                }}
              >
                Select
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

export default SearchBlock;
